import { ChangeEvent, useState } from 'react';
import {
  FormControl,
  FormControlProps,
  FormErrorMessage,
  FormHelperText,
  forwardRef,
  Textarea as ChakraTextarea,
  TextareaProps as ChakraTextareaProps,
} from '@chakra-ui/react';
import { TooltipProps } from '../../Tooltip/Tooltip';
import FormLabel, { FormLabelProps } from '../FormLabel';
import { getDataAttributes } from '../../../utils/utils';

type UsedFormControlProps = Omit<FormControlProps, 'label' | 'onBlur' | 'onChange'>;
type UsedChakraTextProps = Pick<
  ChakraTextareaProps,
  | 'onBlur'
  | 'onChange'
  | 'role'
  | 'name'
  | 'value'
  | 'autoComplete'
  | 'autoFocus'
  | 'maxLength'
  | 'minLength'
  | 'placeholder'
>;

export type TextareaProps = {
  badge?: FormLabelProps['badge'];
  errorText?: string;
  infoTooltipLabel?: string;
  infoTooltipProps?: TooltipProps;
  isLoading?: boolean;
  helperText?: string;
  label?: FormLabelProps['children'];
  maxLength?: number;
  recommendedLength?: number;
  name?: string;
  onBlur?: ChakraTextareaProps['onBlur'];
  onChange?: ChakraTextareaProps['onChange'];
  value?: ChakraTextareaProps['value'];
} & UsedFormControlProps &
  UsedChakraTextProps &
  (
    | {
        recommendedLength?: number;
        maxLength?: never;
      }
    | {
        recommendedLength?: never;
        maxLength?: number;
      }
  );

/**
 * The Textarea component allows you to easily create multi-line text inputs.
 */
const Textarea = forwardRef<TextareaProps, 'div'>((props, ref) => {
  const {
    autoComplete,
    autoFocus,
    badge,
    errorText: errorTextProp,
    helperText,
    infoTooltipLabel,
    infoTooltipProps,
    isDisabled,
    isInvalid,
    isLoading,
    label,
    maxLength,
    minLength,
    name,
    onBlur,
    onChange,
    placeholder,
    recommendedLength,
    resize,
    role,
    value,
    ...rest
  } = props;

  const [valueLength, setValueLength] = useState(value?.toString().length || 0);

  const exceedsCharacterLimitError =
    recommendedLength && valueLength > recommendedLength ? 'Text exceeds the character limit.' : undefined;

  const errorText = errorTextProp || exceedsCharacterLimitError;

  const formControlProps = {
    isDisabled: isDisabled || isLoading,
    isInvalid: isInvalid || !!errorText,
    ...rest,
    ref,
  };
  const textareaProps = {
    autoComplete,
    autoFocus,
    maxLength,
    minLength,
    name,
    onBlur,
    placeholder,
    resize,
    role,
    value,
  };

  const onTextareaChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setValueLength(e.currentTarget.value.length);

    if (onChange) {
      onChange(e);
    }
  };

  const dataAttributes = getDataAttributes(props);

  return (
    <FormControl {...formControlProps}>
      <FormLabel
        badge={badge}
        infoTooltipLabel={infoTooltipLabel}
        infoTooltipProps={infoTooltipProps}
        maxLength={maxLength || recommendedLength}
        valueLength={valueLength}
        withCounter={Boolean(maxLength || recommendedLength)}
      >
        {label}
      </FormLabel>

      <ChakraTextarea {...textareaProps} {...dataAttributes} onChange={onTextareaChange} />
      {errorText && <FormErrorMessage as="p">{errorText}</FormErrorMessage>}
      {!errorText && helperText && <FormHelperText as="p">{helperText}</FormHelperText>}
    </FormControl>
  );
});

export default Textarea;
